import { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import useMediaQuery from '@mui/material/useMediaQuery';
import TableContainer from '@mui/material/TableContainer';
import FiltersPanel from '@pages/Analytics/Conversion/FiltersPanel';
import Button from '@components/Button';
import { Typography } from '@components/Typography';
import Tooltip from '@components/Tooltip';
import { keywordsActions } from '@actions';
import { useTranslation, useLocations } from '@hooks';
import { subtractYears } from '@helpers/dates';
import { RoutePath } from '@constants/routes';
import { StyledTable, SentimentWrapper, SentimentPiece } from './styles';

const GetTableHeader = () => {
    const { t } = useTranslation();
    return [
        {
            key: 0,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('AnalyticsKeywords.keyword')}
                </Typography>
            ),
        },
        {
            key: 1,
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsKeywords.avgRating')}
                </Typography>
            ),
        },
        {
            key: 2,
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsKeywords.numberOfReviews')}
                </Typography>
            ),
        },
        {
            key: 3,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('AnalyticsKeywords.sentiment')}
                </Typography>
            ),
        },
        {
            key: 4,
            component: <Typography align="left" variant="subtitle2"></Typography>,
        },
    ];
};

const GetTableBody = (data, onClickKeyword) => {
    const { t } = useTranslation();
    return data.map(keyword => {
        const positive = keyword.sentiments.Positive || 0;
        const neutral = keyword.sentiments.Neutral || 0;
        const negative = keyword.sentiments.Negative || 0;
        const total = positive + neutral + negative;
        const positivePercent = (positive * 100) / total;
        const neutralPercent = (neutral * 100) / total;
        const negativePercent = (negative * 100) / total;

        const positiveTooltip = positive > 0 ? `${t('FeedbackSurveys.positive')}: ${positive}` : '';
        const neutralTooltip = neutral > 0 ? `${t('FeedbackSurveys.neutral')}: ${neutral}` : '';
        const negativeTooltip = negative > 0 ? `${t('FeedbackSurveys.negative')}: ${negative}` : '';

        const tooltipText = [positiveTooltip, neutralTooltip, negativeTooltip].filter(Boolean).join(', ');

        return [
            {
                component: (
                    <Typography variant="subtitle2" align="left">
                        {keyword.keyword}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="right">
                        {keyword.averageRating.toFixed(2)}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="right">
                        {keyword.numberOfReviews}
                    </Typography>
                ),
            },
            {
                component: (
                    <Tooltip title={tooltipText} placement="top">
                        <SentimentWrapper>
                            <SentimentPiece type={1} percentage={positivePercent} />
                            <SentimentPiece type={0} percentage={neutralPercent} />
                            <SentimentPiece type={2} percentage={negativePercent} />
                        </SentimentWrapper>
                    </Tooltip>
                ),
            },
            {
                component: (
                    <Button variant="outlined" onClick={() => onClickKeyword(keyword.keyword)}>
                        {t('buttons.viewReviews')}
                    </Button>
                ),
                value: true,
            },
        ];
    });
};

const KeywordsPage = () => {
    const { t } = useTranslation();
    const [filter, setFilter] = useState({
        locations: [],
        timeframe: {
            start: subtractYears(new Date(), 1),
            end: new Date(),
        },
    });
    const [filterOpened, setFiltersOpened] = useState(false);
    const [filterNumber, setFilterNumber] = useState(0);
    const [pageRequests, setPageRequests] = useState(0);
    const [rowsPerPageRequests, setRowsPerPageRequests] = useState(20);

    const locations = useSelector(state => state.account?.account?.companies || []);
    const { data, loading, totalCount, pages } = useSelector(state => state.keywords);

    const isMobile = useMediaQuery('@media (max-width: 1024px)');
    const { mapLocationsToFormValue } = useLocations();
    const dispatch = useDispatch();
    const history = useHistory();
    const firstRender = useRef(true);

    useEffect(() => {
        if (firstRender.current) {
            const filterLocations = mapLocationsToFormValue();
            setFilter(prev => ({ ...prev, locations: filterLocations }));
            dispatch(
                keywordsActions.get(
                    {
                        locations: filterLocations.map(item => item.id),
                        from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                        to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
                    },
                    0,
                    rowsPerPageRequests,
                ),
            );
            firstRender.current = false;
        }
    }, [dispatch]);

    const currentQuery = useMemo(() => {
        return {
            locations: filter.locations.map(item => item.id),
            from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
            to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
        };
    }, [filter]);

    const onPageChange = (_, newPage) => {
        setPageRequests(newPage);
        dispatch(keywordsActions.get(currentQuery, newPage, rowsPerPageRequests));
    };

    const onRowsPerPageChange = event => {
        setRowsPerPageRequests(parseInt(event.target.value, 10));
        setPageRequests(0);
        dispatch(keywordsActions.get(currentQuery, 0, parseInt(event.target.value, 10)));
    };

    const onApplyFilters = () => {
        dispatch(keywordsActions.get(currentQuery, 0, rowsPerPageRequests));
        setPageRequests(0);
    };

    const onClickKeyword = keyword => history.push(`${RoutePath.ReviewsManage}?keyword=${keyword}`);

    return (
        <>
            <div style={{ marginBottom: 16 }}>
                <FiltersPanel
                    setFiltersOpened={setFiltersOpened}
                    filtersNumber={filterNumber}
                    filterOpened={filterOpened}
                    locations={locations}
                    filter={filter}
                    setFilter={setFilter}
                    setFilterNumber={setFilterNumber}
                    onApplyFilters={onApplyFilters}
                    type="conversion"
                />
            </div>
            <section style={{ border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: 4, padding: 16 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '16px', color: 'rgba(0, 0, 0, 0.87)' }}>
                    {t('AnalyticsKeywords.tableTitle')}
                </Typography>
                <TableContainer>
                    <StyledTable
                        withHover
                        columnNames={GetTableHeader()}
                        rows={GetTableBody(data, onClickKeyword)}
                        isDataLoading={loading}
                        withPagination
                        isInteractive={!isMobile}
                        isCustomActionComponent
                        paginationProps={{
                            colSpan: 5,
                            count: totalCount,
                            rowsPerPage: rowsPerPageRequests,
                            page: pageRequests,
                            onPageChange,
                            onRowsPerPageChange,
                            labelDisplayedRows: ({ from, to, count }) =>
                                `${from}–${to} ${t('Table.of')} ${
                                    count !== -1 ? count : `${t('Table.moreThan')} ${to}`
                                }`,
                            labelRowsPerPage: <span>{t('Table.rowsPerPage')}</span>,
                            pages,
                        }}
                    />
                </TableContainer>
            </section>
        </>
    );
};

export default KeywordsPage;
